import { type FC, useState } from "react";
import { EmailContentView } from "../../../../components/EmailContentView";
import { LoadingUI } from "../../../../components/Loading";
import type { MatchingResultItem } from "../../../../hooks/use-free-matching-api";
import { useEmailDetailRestApi } from "../../../../hooks/useEmailDetailRestApi";
import {
  type EmailDocument,
  RecruitmentType,
} from "../../../../hooks/useEmailsRestApi";
import { ParsedHRView } from "./ParsedHRView";
import { ParsedMatterView } from "./ParsedMatterView";

// =====================================
// Score helpers
// =====================================

interface ScoreBarProps {
  label: string;
  score: number | null;
}

const ScoreBar: FC<ScoreBarProps> = ({ label, score }) => {
  if (score === null) return null;
  const pct = Math.round(score * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-10 text-gray-500 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-blue-500 h-1.5 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-7 text-right text-gray-600">{pct}%</span>
    </div>
  );
};

/**
 * スコアに応じたバッジCSSクラスを返す
 * @param {number} score スコア値
 * @return {string} バッジCSSクラス
 */
function getScoreBadgeClass(score: number): string {
  if (score >= 0.8) return "badge-success";
  if (score >= 0.6) return "badge-warning";
  return "badge-error";
}

// =====================================
// EmailComparisonPanel
// =====================================

type TabType = "parsed" | "raw";

interface EmailComparisonPanelProps {
  email: EmailDocument;
  label: string;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const EmailComparisonPanel: FC<EmailComparisonPanelProps> = ({
  email,
  label,
  activeTab,
  onTabChange,
}) => {
  const hasParsedData =
    (email.recruitmentType === RecruitmentType.Matter && email.matterColumn) ||
    (email.recruitmentType === RecruitmentType.HumanResource && email.hrColumn);

  return (
    <>
      {/* パネルヘッダー */}
      <div className="px-4 py-3 border-b bg-gray-50 shrink-0">
        <span className="badge badge-sm badge-outline mb-1">{label}</span>
        <h4 className="font-semibold text-sm truncate">{email.subject}</h4>
        <p className="text-xs text-gray-500">
          {email.from.name} ({email.from.address}) | {email.date}
        </p>
      </div>

      {/* タブ */}
      {hasParsedData && (
        <div className="flex border-b shrink-0">
          <button
            onClick={() => onTabChange("parsed")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "parsed"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            type="button"
          >
            整理された情報
          </button>
          <button
            onClick={() => onTabChange("raw")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "raw"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            type="button"
          >
            メール本文
          </button>
        </div>
      )}

      {/* コンテンツ */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "parsed" && hasParsedData ? (
          <>
            {email.recruitmentType === RecruitmentType.Matter &&
              email.matterColumn && (
                <ParsedMatterView matterColumn={email.matterColumn} />
              )}
            {email.recruitmentType === RecruitmentType.HumanResource &&
              email.hrColumn && <ParsedHRView hrColumn={email.hrColumn} />}
          </>
        ) : (
          <EmailContentView
            content={email.content}
            freeWords={[]}
            highlighterType="Modal"
          />
        )}
      </div>
    </>
  );
};

// =====================================
// DetailedComparisonModal
// =====================================

export interface DetailedComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceEmail: EmailDocument;
  matchingResult: MatchingResultItem;
}

export const DetailedComparisonModal: FC<DetailedComparisonModalProps> = ({
  isOpen,
  onClose,
  sourceEmail,
  matchingResult,
}) => {
  const [sourceTab, setSourceTab] = useState<TabType>("parsed");
  const [matchedTab, setMatchedTab] = useState<TabType>("parsed");

  const {
    data: matchedData,
    loading: matchedLoading,
    error: matchedError,
  } = useEmailDetailRestApi({
    variables: { id: matchingResult.matchedEmailId! },
    skip: !isOpen || !matchingResult.matchedEmailId,
  });
  const matchedEmail = matchedData?.email ?? null;

  if (!isOpen) return null;

  const totalPct = Math.round(matchingResult.totalScore * 100);
  const sourceLabel =
    sourceEmail.recruitmentType === RecruitmentType.Matter
      ? "案件メール"
      : "人材メール";
  const matchedLabel =
    sourceEmail.recruitmentType === RecruitmentType.Matter
      ? "人材メール"
      : "案件メール";

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-7xl w-full h-[85vh] flex flex-col p-0">
        {/* ヘッダー: スコアサマリー */}
        <div className="bg-base-200 px-6 py-3 border-b flex items-center justify-between shrink-0">
          <h3 className="font-bold text-lg">詳細比較</h3>
          <div className="flex items-center gap-4">
            <span
              className={`badge ${getScoreBadgeClass(matchingResult.totalScore)}`}
            >
              総合 {totalPct}%
            </span>
            <div className="flex gap-3">
              <ScoreBar label="スキル" score={matchingResult.skillScore} />
              <ScoreBar label="単価" score={matchingResult.rewardScore} />
              <ScoreBar label="勤務地" score={matchingResult.locationScore} />
              <ScoreBar label="稼働" score={matchingResult.availabilityScore} />
              <ScoreBar label="商流" score={matchingResult.depthScore} />
            </div>
          </div>
          <button
            className="btn btn-sm btn-ghost"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        {/* AI分析帯 */}
        {(matchingResult.aiReasoning ||
          matchingResult.aiHighlights?.length ||
          matchingResult.aiRisks?.length) && (
          <div className="px-6 py-2 bg-blue-50 border-b text-sm shrink-0">
            {matchingResult.aiReasoning && (
              <p className="text-gray-700">{matchingResult.aiReasoning}</p>
            )}
            {(matchingResult.aiHighlights?.length ||
              matchingResult.aiRisks?.length) && (
              <div className="flex flex-wrap gap-1 mt-1">
                {matchingResult.aiHighlights?.map(h => (
                  <span
                    key={h}
                    className="badge badge-sm badge-success badge-outline"
                  >
                    {h}
                  </span>
                ))}
                {matchingResult.aiRisks?.map(r => (
                  <span
                    key={r}
                    className="badge badge-sm badge-warning badge-outline"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 左右パネル */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左: 起点メール */}
          <div className="w-1/2 flex flex-col border-r overflow-hidden">
            <EmailComparisonPanel
              email={sourceEmail}
              label={sourceLabel}
              activeTab={sourceTab}
              onTabChange={setSourceTab}
            />
          </div>

          {/* 右: マッチ先メール */}
          <div className="w-1/2 flex flex-col overflow-hidden">
            {matchedLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingUI type="Grid" />
              </div>
            ) : matchedError ? (
              <div className="flex-1 flex items-center justify-center text-red-500">
                <p>メールの取得に失敗しました</p>
              </div>
            ) : matchedEmail ? (
              <EmailComparisonPanel
                email={matchedEmail}
                label={matchedLabel}
                activeTab={matchedTab}
                onTabChange={setMatchedTab}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
};
